import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Printer, FileText, Loader2 } from "lucide-react";
import axios from "axios";
import { useAssessment } from "../context/AssessmentContext";
import { PageShell } from "../components/PageShell";
import { TopProgressBar } from "../components/TopProgressBar";
import { analyzeAnswers, buildInformationRequired } from "../lib/scopeEngine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScopeOfWork() {
  const navigate = useNavigate();
  const { client, answers, customNotes, setCustomNotes } = useAssessment();
  const analysis = useMemo(() => analyzeAnswers(answers, client.stage), [answers, client.stage]);
  const information = useMemo(() => buildInformationRequired(answers, client), [answers, client]);
  const [downloading, setDownloading] = useState(false);

  const today = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

  const handleExportWord = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/generate-scope`,
        {
          client,
          challenges: analysis.challenges,
          recommended_services: analysis.recommendedServices,
          focus_areas: analysis.focusAreas,
          risk_summary: analysis.riskSummary,
          complexity: analysis.complexity,
          information_required: information,
          engagement_style: analysis.engagementStyle,
          custom_notes: customNotes,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (client.business_name || "Client").replace(/\s+/g, "_");
      a.download = `EYB_Scope_of_Work_${safe}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Something went wrong generating the Word document. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrintPDF = () => {
    window.print();
  };

  return (
    <PageShell progressBar={<TopProgressBar value={100} />} max="max-w-6xl">
      <div data-testid="scope-page">
        {/* Sticky action bar */}
        <div className="no-print sticky top-16 z-30 -mx-6 md:-mx-10 mb-10 bg-[#F5F7FA]/95 backdrop-blur-sm border-b border-[#E5E7EB] px-6 md:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
          <button
            data-testid="back-to-results-btn"
            onClick={() => navigate("/results")}
            className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#0A1A2F] text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back to summary
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="export-pdf-btn"
              onClick={handlePrintPDF}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-sm border border-[#2F5D8C] text-[#2F5D8C] hover:bg-white transition-colors text-xs tracking-[0.2em] uppercase"
            >
              <Printer className="h-4 w-4" strokeWidth={1.5} />
              Print / PDF
            </button>
            <button
              data-testid="export-word-btn"
              onClick={handleExportWord}
              disabled={downloading}
              className="inline-flex items-center gap-2 bg-[#0A1A2F] hover:bg-[#2F5D8C] disabled:bg-[#9CA3AF] text-white px-5 py-3 rounded-sm transition-colors text-xs tracking-[0.2em] uppercase"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={1.5} />}
              {downloading ? "Generating..." : "Export Word"}
            </button>
          </div>
        </div>

        {/* Advisor notes (collapsible inline) */}
        <div className="no-print mb-8 bg-white border border-dashed border-[#1FA8E0]/40 rounded-lg p-5">
          <label className="block">
            <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#1FA8E0] mb-2">
              <FileText className="h-4 w-4" strokeWidth={1.5} />
              Optional advisor notes
            </div>
            <textarea
              data-testid="advisor-notes"
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              rows={2}
              placeholder="Anything you'd like to add before exporting (e.g. context, agreed fee approach)..."
              className="w-full bg-[#F5F7FA] border border-[#E5E7EB] rounded-sm px-4 py-3 text-[#0A1A2F] text-sm placeholder-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1FA8E0] focus:border-[#1FA8E0] transition-all resize-none"
            />
          </label>
        </div>

        {/* Document preview */}
        <motion.article
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white paper-shadow print-container rounded-lg overflow-hidden"
        >
          {/* Cover band */}
          <div className="px-10 md:px-16 pt-16 pb-12 bg-white border-b border-[#E5E7EB]">
            <div className="flex items-center gap-3 mb-12">
              <img src="/eyb-logo.png" alt="EYB" className="h-10 w-10 object-contain" />
              <div>
                <div className="font-display font-bold text-[15px] tracking-tight text-[#0A1A2F]">EYB Consulting</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#6B7280]">Strategic Advisory</div>
              </div>
            </div>

            <div className="text-[11px] tracking-[0.28em] uppercase text-[#1FA8E0] mb-4">Draft Document</div>
            <h1 className="font-display font-bold text-5xl md:text-6xl text-[#0A1A2F] leading-tight tracking-tight">
              Scope of Work
            </h1>
            <p className="mt-3 font-display font-medium text-xl md:text-2xl text-[#2F5D8C]">
              Draft engagement proposal & advisory plan
            </p>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-[#E5E7EB]">
              <Meta label="Prepared for" value={client.business_name || "—"} />
              <Meta label="Industry" value={client.industry || "—"} />
              <Meta label="Location" value={client.state_location || "—"} />
              <Meta label="Date" value={today} />
            </div>
          </div>

          <div className="px-10 md:px-16 py-12 space-y-12">
            <Section title="Client Overview">
              {client.stage === "pre_start" ? (
                <p>
                  {client.business_name || "The client"} is a pre-launch venture in the {client.industry || "—"} sector
                  {client.state_location ? `, based in ${client.state_location}` : ""}.
                  This scope of work has been prepared to support the planning, validation and setup work required ahead of trading.
                </p>
              ) : (
                <p>
                  {client.business_name || "The client"} operates in the {client.industry || "—"} sector
                  {client.state_location ? `, based in ${client.state_location}` : ""}.
                  {client.employees ? ` The business currently has ${client.employees}` : ""}
                  {client.revenue_range ? ` and reports an annual revenue range of ${client.revenue_range}.` : "."}
                  {client.accounting_software ? ` Existing financial systems include ${client.accounting_software}.` : ""}
                </p>
              )}
              {client.contact_person && (
                <p className="mt-3">Primary point of contact: <span className="text-[#0A1A2F] font-medium">{client.contact_person}</span>.</p>
              )}
            </Section>

            <Section title="Key Challenges Identified">
              {analysis.challenges.length ? (
                <ul className="space-y-3 list-none pl-0">
                  {analysis.challenges.map((c, i) => (
                    <li key={c} className="flex gap-4">
                      <span className="font-display font-bold text-[#1FA8E0] text-lg leading-tight w-8">0{i + 1}</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic text-[#6B7280]">No specific challenges flagged. An initial discovery session is recommended.</p>
              )}
            </Section>

            <Section title="Business Risk Summary">
              <p>{analysis.riskSummary}</p>
              <p className="mt-3">
                <span className="text-[11px] tracking-[0.22em] uppercase text-[#0A1A2F] font-medium">Estimated complexity: </span>
                <span className="text-[#2F5D8C]">{analysis.complexity}</span>
              </p>
            </Section>

            <Section title="Recommended Scope of Work">
              {analysis.recommendedServices.length ? (
                <div className="space-y-5">
                  {analysis.recommendedServices.map((s) => (
                    <div key={s.title} className="pl-5 border-l-2 border-[#1FA8E0]">
                      <div className="font-display font-bold text-xl text-[#0A1A2F] leading-snug">{s.title}</div>
                      <div className="text-[#6B7280] text-sm leading-relaxed mt-1">{s.description}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="italic text-[#6B7280]">Scope items to be tailored after initial discovery.</p>
              )}
            </Section>

            <Section title="Suggested Focus Areas">
              {analysis.focusAreas.length ? (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 list-none pl-0">
                  {analysis.focusAreas.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#1FA8E0] mt-2" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic text-[#6B7280]">To be confirmed with client priorities.</p>
              )}
            </Section>

            <Section title="Information Required from Client">
              <ul className="space-y-2 list-none pl-0">
                {information.map((i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[#1FA8E0] mt-1">›</span>
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Proposed Engagement Style">
              <p>{analysis.engagementStyle}</p>
            </Section>

            {customNotes && (
              <Section title="Advisor Notes">
                <p className="whitespace-pre-wrap">{customNotes}</p>
              </Section>
            )}

            <Section title="Limitations & Disclaimer" muted>
              <p className="text-[13px] italic text-[#6B7280] leading-relaxed">
                This document represents a recommended scope of work prepared as a draft for client review.
                It does not constitute a binding engagement, financial advice, or a guarantee of outcomes.
                Final terms, fees and deliverables will be confirmed in a separate engagement letter signed by
                both parties. All recommendations are based on the information provided by the client during the
                diagnostic assessment and are subject to further review by an EYB Consulting advisor.
              </p>
            </Section>
          </div>

          {/* Document footer */}
          <div className="px-10 md:px-16 py-6 bg-[#F5F7FA] border-t border-[#E5E7EB] text-center">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#6B7280]">
              EYB Consulting · Strategic Advisory for Growing Businesses · Confidential
            </div>
          </div>
        </motion.article>
      </div>
    </PageShell>
  );
}

const Meta = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mb-1">{label}</div>
    <div className="text-[#0A1A2F] text-sm font-medium">{value}</div>
  </div>
);

const Section = ({ title, children, muted }) => (
  <section>
    <h2 className={`font-display font-bold text-2xl md:text-3xl mb-5 leading-tight ${muted ? "text-[#2F5D8C]" : "text-[#0A1A2F]"}`}>
      {title}
    </h2>
    <div className="h-px w-full bg-[#E5E7EB] mb-6" />
    <div className="text-[#374151] text-[15px] leading-relaxed font-sans-body">{children}</div>
  </section>
);
